import React, { createContext, useContext, useState, useEffect } from 'react';
import { useGameDataContext } from './GameDataContext';
import fetchClient from '../utils/fetchClient';

const EnergyContext = createContext();

export const useEnergyContext = () => {
    const context = useContext(EnergyContext);
    if (!context) {
        throw new Error('useEnergyContext must be used within an EnergyProvider');
    }
    return context;
};

export const EnergyProvider = ({ children }) => {
    const { energyStatus, handleGameDataUpdate, updateEvents } = useGameDataContext();
    const [energyData, setEnergyData] = useState({
        yang_energy: 0,
        yin_energy: 0,
        total_energy: 0,
        yang_percent: 0,
        yin_percent: 0,
        last_update: null,
        current_time: null,
        server_time: null,
        server_timezone: null,
        python_time: null,
        original_yang_energy: 0,
        original_yin_energy: 0,
        original_total_energy: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isMounted, setIsMounted] = useState(true);

    // Синхронизируем локальное состояние с данными из GameDataContext
    useEffect(() => {
        if (energyStatus && isMounted) {
            setEnergyData(energyStatus);
            setError(null);
            setLoading(false);
        }
    }, [energyStatus, isMounted]);

    const fetchEnergyStatus = async () => {
        try {
            if (!isMounted) return;
            setLoading(true);
            // Вместо прямого API вызова используем событие обновления
            handleGameDataUpdate(updateEvents.MANUAL_REFRESH);
        } catch (err) {
            console.error('Error fetching energy status:', err);
            if (isMounted) {
                const errorMessage = err.message || err.response?.data?.detail || 'Failed to fetch energy status';
                setError(errorMessage);
            }
        } finally {
            if (isMounted) {
                setLoading(false);
            }
        }
    };

    const spendEnergy = async (energyPoints, reason = 'manual_spent') => {
        try {
            setLoading(true);
            const response = await fetchClient.post('/user-game-data/spend-energy-v2', {
                energy_points: energyPoints,
                reason: reason
            });
            
            // Обновляем данные через событие
            handleGameDataUpdate(updateEvents.ENERGY_SPENT, { energyPoints, reason });
            
            return response;
        } catch (err) {
            console.error('Error spending energy:', err);
            let errorMessage = 'Failed to spend energy';
            
            if (err.response?.data?.detail) {
                errorMessage = err.response.data.detail;
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.message) {
                errorMessage = err;
            } else if (typeof err === 'string') {
                errorMessage = err;
            }
            
            console.log('Detailed error info:', {
                err,
                response: err.response,
                data: err.response?.data,
                detail: err.response?.data?.detail,
                message: err.response?.data?.message
            });
            
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const addYangEnergy = async (energyPoints = 1, reason = 'manual_add') => {
        try {
            setLoading(true);
            const response = await fetchClient.post('/user-game-data/add-yang-energy', {
                energy_points: energyPoints,
                reason: reason
            });
            
            // Обновляем данные через событие
            handleGameDataUpdate(updateEvents.ENERGY_ADDED, { energyPoints, reason });
            
            return response;
        } catch (err) {
            console.error('Error adding yang energy:', err);
            let errorMessage = 'Failed to add yang energy';
            
            if (err.response?.data?.detail) {
                errorMessage = err.response.data.detail;
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.message) {
                errorMessage = err.message;
            } else if (typeof err === 'string') {
                errorMessage = err;
            }
            
            console.log('Detailed error info:', {
                err,
                response: err.response,
                data: err.response?.data,
                detail: err.response?.data?.detail,
                message: err.response?.data?.message
            });
            
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const addYinEnergy = async (energyPoints = 1, reason = 'manual_add') => {
        try {
            setLoading(true);
            const response = await fetchClient.post('/user-game-data/add-yin-energy', {
                energy_points: energyPoints,
                reason: reason
            });
            
            // Обновляем данные через событие
            handleGameDataUpdate(updateEvents.ENERGY_ADDED, { energyPoints, reason });
            
            return response;
        } catch (err) {
            console.error('Error adding yin energy:', err);
            let errorMessage = 'Failed to add yin energy';
            
            if (err.response?.data?.detail) {
                errorMessage = err.response.data.detail;
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.message) {
                errorMessage = err.message;
            } else if (typeof err === 'string') {
                errorMessage = err;
            }
            
            console.log('Detailed error info:', {
                err,
                response: err.response,
                data: err.response?.data,
                detail: err.response?.data?.detail,
                message: err.response?.data?.message
            });
            
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const updateAfterSpin = async (spinResult) => {
        try {
            // Обновляем данные через событие
            handleGameDataUpdate(updateEvents.ENERGY_SPIN, spinResult);
            console.log('Данные обновлены после крутки:', spinResult);
        } catch (err) {
            console.error('Error updating after spin:', err);
        }
    };

    // Инициализация при монтировании
    useEffect(() => {
        setIsMounted(true);
        // Данные уже загружаются через GameDataContext
        // fetchEnergyStatus(); // ❌ Убрано - данные загружаются автоматически

        return () => {
            setIsMounted(false);
        };
    }, [isMounted]);

    // Добавляем обработчик события обновления энергии
    useEffect(() => {
        const handleRefreshEnergy = () => {
            console.log('🔄 Обновляем данные энергии по событию');
            fetchEnergyStatus();
        };

        window.addEventListener('refreshEnergyData', handleRefreshEnergy);

        return () => {
            window.removeEventListener('refreshEnergyData', handleRefreshEnergy);
        };
    }, []);

    const value = {
        energyData,
        loading,
        error,
        fetchEnergyStatus,
        spendEnergy,
        addYangEnergy,
        addYinEnergy,
        updateAfterSpin
    };

    return (
        <EnergyContext.Provider value={value}>
            {children}
        </EnergyContext.Provider>
    );
}; 